import * as THREE from 'three';

/**
 * SketchRenderer3D - Renders 2D sketches onto 3D planes
 *
 * Phase 3.5: Two approaches supported:
 * 1. Canvas Texture (default) - Renders sketch to 2D canvas, applies as texture
 * 2. Line Geometry (alternative) - Converts paths to Three.js Line geometry
 *
 * This enables drawing directly on 3D planes in the unified view.
 */
export class SketchRenderer3D {
    /**
     * @param {string} mode - Rendering mode: 'canvas' or 'lines'
     */
    constructor(mode = 'canvas') {
        this.mode = mode;

        if (this.mode === 'canvas') {
            // Create offscreen canvas for sketch rendering
            this.canvas = document.createElement('canvas');
            this.canvas.width = 2048;
            this.canvas.height = 2048;
            this.ctx = this.canvas.getContext('2d');

            // Create texture from canvas
            this.texture = new THREE.CanvasTexture(this.canvas);
            this.texture.minFilter = THREE.LinearFilter;
            this.texture.magFilter = THREE.LinearFilter;
        }

        // Track sketch meshes by plane
        this.sketchMeshesByPlane = new Map(); // planeIndex -> mesh/lines
    }

    /**
     * Render sketch to a base plane using canvas texture approach
     *
     * @param {Object} sketchState - Sketch state with paths, splines, closedShapes
     * @param {Object} basePlane - Base plane object with position and normal
     * @param {number} planeIndex - Index of the plane
     * @param {THREE.Scene} scene - Three.js scene to add mesh to
     * @returns {THREE.Mesh|Array<THREE.Line>} Created mesh or line array
     */
    renderSketchToPlane(sketchState, basePlane, planeIndex, scene) {
        if (this.mode === 'canvas') {
            return this.renderWithCanvas(sketchState, basePlane, planeIndex, scene);
        } else {
            return this.renderWithLines(sketchState, basePlane, planeIndex, scene);
        }
    }

    /**
     * Render using canvas texture approach
     */
    renderWithCanvas(sketchState, basePlane, planeIndex, scene) {
        // Clear canvas
        this.ctx.fillStyle = 'rgba(0,0,0,0)';
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Setup canvas transform
        // Canvas coordinates: center at (1024, 1024), scale 1px = 0.01 world units
        const scale = this.canvas.width / 20; // 20 world units = 2048 pixels -> 102.4 pixels/unit
        this.ctx.save();
        this.ctx.translate(this.canvas.width / 2, this.canvas.height / 2);
        this.ctx.scale(scale, -scale); // Flip Y to match world coordinates

        // Render all paths
        if (sketchState.paths) {
            sketchState.paths.forEach(path => {
                this.renderPathToCanvas(path);
            });
        }

        // Render all splines (Bezier curves)
        if (sketchState.splines) {
            sketchState.splines.forEach((spline) => {
                this.renderSplineToCanvas(spline);
            });
        }

        // Render closed shapes with fill
        if (sketchState.closedShapes) {
            sketchState.closedShapes.forEach(shape => {
                this.renderShapeToCanvas(shape);
            });
        }

        this.ctx.restore();

        // Update texture
        this.texture.needsUpdate = true;

        // Remove old mesh if exists
        if (this.sketchMeshesByPlane.has(planeIndex)) {
            const oldMesh = this.sketchMeshesByPlane.get(planeIndex);
            scene.remove(oldMesh);
            if (oldMesh.geometry) oldMesh.geometry.dispose();
            if (oldMesh.material && oldMesh.material.map !== this.texture) {
                oldMesh.material.dispose();
            }
        }

        // Create plane mesh with texture
        const planeSize = 20; // 20 world units
        const geometry = new THREE.PlaneGeometry(planeSize, planeSize);

        const material = new THREE.MeshBasicMaterial({
            map: this.texture,
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide,
            depthTest: true,
            depthWrite: false // Don't write to depth buffer to avoid z-fighting
        });

        const mesh = new THREE.Mesh(geometry, material);

        // Position mesh on the base plane
        mesh.position.copy(basePlane.position);

        // Offset slightly forward to prevent z-fighting with plane visualization
        const offset = 0.001;
        mesh.position.z += offset;

        // Apply rotation from base plane if any
        if (basePlane.rotation) {
            mesh.rotation.copy(basePlane.rotation);
        }

        scene.add(mesh);
        this.sketchMeshesByPlane.set(planeIndex, mesh);

        return mesh;
    }

    /**
     * Render a path to canvas (linear segments)
     */
    renderPathToCanvas(path) {
        if (!path.points || path.points.length === 0) return;

        this.ctx.strokeStyle = path.color || '#000000';
        this.ctx.lineWidth = (path.strokeWeight || 2) / 100; // Convert to world units
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        this.ctx.beginPath();

        path.points.forEach((point, i) => {
            // Points are in canvas pixels, convert to world units
            const x = point.x * 0.01;
            const y = point.y * 0.01;

            if (i === 0) {
                this.ctx.moveTo(x, y);
            } else {
                this.ctx.lineTo(x, y);
            }
        });

        this.ctx.stroke();
    }

    /**
     * Render a spline to canvas (Bezier curves with offset path)
     */
    renderSplineToCanvas(spline) {
        // Build the offset path if not already built
        if (!spline.isBuilt && spline.centrePath?.size() > 0) {
            spline.build();
        }

        // Render the offset outline path (the "brush" shape)
        const offsetPath = spline.path;
        if (offsetPath && offsetPath.points && offsetPath.points.length > 0) {
            // Draw filled offset path
            this.ctx.fillStyle = spline.fill || 'rgba(76, 175, 80, 0.3)';
            this.ctx.strokeStyle = spline.stroke || '#4CAF50';
            this.ctx.lineWidth = (spline.strokeWeight || 2) / 100;
            this.ctx.lineCap = 'round';
            this.ctx.lineJoin = 'round';

            this.ctx.beginPath();

            offsetPath.points.forEach((point, i) => {
                const x = point.x * 0.01;
                const y = point.y * 0.01;

                if (i === 0) {
                    this.ctx.moveTo(x, y);
                } else {
                    this.ctx.lineTo(x, y);
                }
            });

            this.ctx.closePath();
            this.ctx.fill();
            this.ctx.stroke();
        } else if (spline.centrePath?.points && spline.centrePath.points.length > 0) {
            // Fallback: render center line if offset path not available (< 2 points)
            const points = spline.centrePath.points;

            this.ctx.strokeStyle = spline.color || '#ff0000';
            this.ctx.lineWidth = (spline.strokeWeight || 2) / 100;
            this.ctx.lineCap = 'round';
            this.ctx.lineJoin = 'round';

            this.ctx.beginPath();

            let prevPoint = null;

            points.forEach((point, i) => {
                const x = point.x * 0.01;
                const y = point.y * 0.01;

                if (i === 0) {
                    this.ctx.moveTo(x, y);
                } else if (point.controlPoint2 && prevPoint && prevPoint.controlPoint1) {
                    // Bezier curve
                    const cp1x = prevPoint.controlPoint1.x * 0.01;
                    const cp1y = prevPoint.controlPoint1.y * 0.01;
                    const cp2x = point.controlPoint2.x * 0.01;
                    const cp2y = point.controlPoint2.y * 0.01;

                    this.ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
                } else {
                    // Linear segment (fallback)
                    this.ctx.lineTo(x, y);
                }

                prevPoint = point;
            });

            this.ctx.stroke();
        }
    }

    /**
     * Render a closed shape to canvas with fill
     */
    renderShapeToCanvas(shape) {
        if (!shape.getPath || !shape.getPath()) return;

        const path = shape.getPath();
        if (!path.points || path.points.length === 0) return;

        // Draw fill
        this.ctx.fillStyle = shape.fill || 'rgba(76, 175, 80, 0.3)';
        this.ctx.beginPath();

        let prevPoint = null;

        path.points.forEach((point, i) => {
            const x = point.x * 0.01;
            const y = point.y * 0.01;

            if (i === 0) {
                this.ctx.moveTo(x, y);
            } else if (point.controlPoint2 && prevPoint && prevPoint.controlPoint1) {
                const cp1x = prevPoint.controlPoint1.x * 0.01;
                const cp1y = prevPoint.controlPoint1.y * 0.01;
                const cp2x = point.controlPoint2.x * 0.01;
                const cp2y = point.controlPoint2.y * 0.01;
                this.ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
            } else {
                this.ctx.lineTo(x, y);
            }

            prevPoint = point;
        });

        this.ctx.closePath();
        this.ctx.fill();

        // Draw stroke
        this.ctx.strokeStyle = shape.stroke || '#4CAF50';
        this.ctx.lineWidth = (shape.strokeWeight || 2) / 100;
        this.ctx.stroke();
    }

    /**
     * Render using Three.js Line geometry approach (alternative)
     */
    renderWithLines(sketchState, basePlane, planeIndex, scene) {
        // Remove old lines if exist
        if (this.sketchMeshesByPlane.has(planeIndex)) {
            const oldLines = this.sketchMeshesByPlane.get(planeIndex);
            oldLines.forEach(line => {
                scene.remove(line);
                if (line.geometry) line.geometry.dispose();
                if (line.material) line.material.dispose();
            });
        }

        const lines = [];
        const zOffset = basePlane.position.z + 0.001;

        // Render paths as lines
        if (sketchState.paths) {
            sketchState.paths.forEach(path => {
                if (!path.points || path.points.length < 2) return;

                const points = path.points.map(p => new THREE.Vector3(
                    p.x * 0.01,
                    p.y * 0.01,
                    zOffset
                ));

                const geometry = new THREE.BufferGeometry().setFromPoints(points);
                const material = new THREE.LineBasicMaterial({
                    color: path.color ? new THREE.Color(path.color) : 0x000000,
                    linewidth: path.strokeWeight || 2
                });

                const line = new THREE.Line(geometry, material);
                scene.add(line);
                lines.push(line);
            });
        }

        // Render splines as curves
        if (sketchState.splines) {
            sketchState.splines.forEach(spline => {
                if (!spline.points || spline.points.length < 2) return;

                const points = [];
                let prevPoint = null;

                spline.points.forEach((point, i) => {
                    if (i === 0) {
                        points.push(new THREE.Vector3(
                            point.x * 0.01,
                            point.y * 0.01,
                            zOffset
                        ));
                    } else if (point.controlPoint2 && prevPoint && prevPoint.controlPoint1) {
                        // Create Bezier curve
                        const curve = new THREE.CubicBezierCurve3(
                            new THREE.Vector3(prevPoint.x * 0.01, prevPoint.y * 0.01, zOffset),
                            new THREE.Vector3(prevPoint.controlPoint1.x * 0.01, prevPoint.controlPoint1.y * 0.01, zOffset),
                            new THREE.Vector3(point.controlPoint2.x * 0.01, point.controlPoint2.y * 0.01, zOffset),
                            new THREE.Vector3(point.x * 0.01, point.y * 0.01, zOffset)
                        );

                        // Tessellate curve
                        const curvePoints = curve.getPoints(10);
                        points.push(...curvePoints);
                    } else {
                        points.push(new THREE.Vector3(
                            point.x * 0.01,
                            point.y * 0.01,
                            zOffset
                        ));
                    }

                    prevPoint = point;
                });

                if (points.length > 0) {
                    const geometry = new THREE.BufferGeometry().setFromPoints(points);
                    const material = new THREE.LineBasicMaterial({
                        color: spline.color ? new THREE.Color(spline.color) : 0x000000,
                        linewidth: spline.strokeWeight || 2
                    });

                    const line = new THREE.Line(geometry, material);
                    scene.add(line);
                    lines.push(line);
                }
            });
        }

        this.sketchMeshesByPlane.set(planeIndex, lines);
        return lines;
    }

    /**
     * Clear all sketch renderings
     * @param {THREE.Scene} scene - Three.js scene
     */
    clearAll(scene) {
        this.sketchMeshesByPlane.forEach((meshOrLines, planeIndex) => {
            if (Array.isArray(meshOrLines)) {
                // Line mode
                meshOrLines.forEach(line => {
                    scene.remove(line);
                    if (line.geometry) line.geometry.dispose();
                    if (line.material) line.material.dispose();
                });
            } else {
                // Canvas mode
                scene.remove(meshOrLines);
                if (meshOrLines.geometry) meshOrLines.geometry.dispose();
                if (meshOrLines.material && meshOrLines.material.map !== this.texture) {
                    meshOrLines.material.dispose();
                }
            }
        });

        this.sketchMeshesByPlane.clear();
    }

    /**
     * Remove sketch rendering for a specific plane
     * @param {number} planeIndex - Plane index
     * @param {THREE.Scene} scene - Three.js scene
     */
    removePlane(planeIndex, scene) {
        if (this.sketchMeshesByPlane.has(planeIndex)) {
            const meshOrLines = this.sketchMeshesByPlane.get(planeIndex);

            if (Array.isArray(meshOrLines)) {
                meshOrLines.forEach(line => {
                    scene.remove(line);
                    if (line.geometry) line.geometry.dispose();
                    if (line.material) line.material.dispose();
                });
            } else {
                scene.remove(meshOrLines);
                if (meshOrLines.geometry) meshOrLines.geometry.dispose();
                if (meshOrLines.material && meshOrLines.material.map !== this.texture) {
                    meshOrLines.material.dispose();
                }
            }

            this.sketchMeshesByPlane.delete(planeIndex);
        }
    }

    /**
     * Get rendering mode
     * @returns {string} 'canvas' or 'lines'
     */
    getMode() {
        return this.mode;
    }

    /**
     * Cleanup resources
     */
    dispose() {
        if (this.texture) {
            this.texture.dispose();
        }

        // Note: individual meshes/lines are disposed in clearAll/removePlane
    }
}
